/* eslint-disable import/no-unresolved */
import styled from "styled-components"
import { AnimatePresence, motion, useAnimationControls } from "framer-motion"

import Minimap from "./Minimap"
import useContent from "../../shared/hooks/useContent"
import Poi from "./Poi"
import { useWindowSize } from "@react-hook/window-size"
import { Parallax as ReactSpringParallax, ParallaxLayer } from "@react-spring/parallax"
import { useEffect, useRef, useState } from "react"
import { INTRO_START } from "../../shared/constants"
import VideoPlayer from "./Poi/VideoPlayer"

const PADDING_IN_VW = 50

const Background = styled(ReactSpringParallax)`
  position: relative;
  background-color: #c5f9ed;
  background: linear-gradient(to bottom, #c5f9ed 15%, #e7ce9d 100%);
  height: 100%;

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  &::-webkit-scrollbar {
    display: none;
  }
`

const ScaleContainer = styled(motion.div)`
  position: absolute;
  inset: 0px;
  overflow: hidden;
`

const Layer = styled.div`
  position: absolute;
  inset: 0px;
  background-size: 100% auto;
  background-repeat: repeat-x;
  background-position: 0% 100%;
  width: ${({ width }) => width}vw;
  height: 100%;
`

const Layer1 = styled(Layer)`
  background-image: url("../assets/images/layer1.png");
  background-size: 50% auto;
`

const Layer2 = styled(Layer)`
  background-image: url("../assets/images/layer2.png");
`

const Layer3 = styled(Layer)`
  background-image: url("../assets/images/layer3.png");
  background-position: 5% 50%;
`

const animations = {
  variants: {
    scaled: (normalizedScrollPosition) => ({
      scale: 1.2,
      originX: `${10 + normalizedScrollPosition * 80}%`,
      transition: {
        scale: { duration: 1 },
        originX: { duration: 0 },
      },
    }),
    default: (normalizedScrollPosition) => ({
      scale: 1,
      originX: `${10 + normalizedScrollPosition * 80}%`,
      transition: {
        scale: { duration: 0.5 },
        originX: { duration: 0 },
      },
    }),
  },
  initial: "default",
  exit: "default",
}

const Parallax = ({ appState, setIsHintVisible, isHintVisible }) => {
  const pois = useContent({ path: "pois" })
  const [windowWidth, windowHeight] = useWindowSize()
  const parallaxContainerRef = useRef(null)
  const normalizedScrollPositionRef = useRef(null)

  const [sceneWidth, setSceneWidth] = useState(windowWidth > windowHeight ? 3 : 7)
  const [videoDetails, setVideoDetails] = useState({})

  useEffect(() => {
    setSceneWidth(windowWidth > windowHeight ? 3 : 7)
  }, [windowWidth, windowHeight])

  useEffect(() => {
    if (setIsHintVisible && parallaxContainerRef.current) {
      const updateScroll = () => setIsHintVisible(false)
      const parallaxContainer = parallaxContainerRef.current
      parallaxContainer.container.current.addEventListener("scroll", updateScroll)
      return () => {
        if (parallaxContainer.container.current) {
          parallaxContainer.container.current.removeEventListener("scroll", updateScroll)
        }
      }
    }
  }, [parallaxContainerRef, setIsHintVisible, windowWidth])

  useEffect(() => {
    const parallaxContainer = parallaxContainerRef.current
    let timer
    const updateScroll = () => {
      const currentScrollPosition = parallaxContainer.current
      const normalizedScrollPosition = currentScrollPosition / (windowWidth * (sceneWidth - 1))
      timer = setTimeout(() => {
        normalizedScrollPositionRef.current = normalizedScrollPosition
      }, 500)
    }
    parallaxContainer.container.current.addEventListener("scroll", updateScroll)
    return () => {
      clearTimeout(timer)
      if (parallaxContainer.container.current) {
        parallaxContainer.container.current.removeEventListener("scroll", updateScroll)
      }
    }
  }, [parallaxContainerRef, sceneWidth, setIsHintVisible, windowWidth])

  const onAnimationComplete = (variantName) => {
    if (variantName === "enter") {
      parallaxContainerRef.current.scrollTo(normalizedScrollPositionRef.current * (sceneWidth - 1))
    }
  }

  return (
    <>
      <AnimatePresence>
        <motion.div key={sceneWidth} onAnimationComplete={onAnimationComplete} animate={"enter"}>
          <Background
            ref={parallaxContainerRef}
            pages={sceneWidth}
            horizontal
            config={{ tension: 0 }}
          >
            <ScaleContainer
              {...animations}
              custom={normalizedScrollPositionRef.current}
              animate={videoDetails.shallPlayVideo ? "scaled" : "default"}
            >
              <ParallaxLayer factor={sceneWidth} offset={0} speed={-0.5}>
                <Layer3 width={getLayerWidthInPercent(-0.5, sceneWidth)} />
              </ParallaxLayer>
              <ParallaxLayer factor={sceneWidth} offset={0} speed={0}>
                <Layer2 width={getLayerWidthInPercent(0, sceneWidth)} />
              </ParallaxLayer>
              <ParallaxLayer factor={sceneWidth} offset={0} speed={1}>
                <Layer1 width={getLayerWidthInPercent(1, sceneWidth)} />
              </ParallaxLayer>
            </ScaleContainer>

            {Object.values(pois).map((poi) => {
              return (
                <Poi
                  key={poi.id}
                  isCaptionVisible={appState !== INTRO_START && !isHintVisible}
                  name={poi.name}
                  setVideoDetails={setVideoDetails}
                  isPlayingVideo={videoDetails?.shallPlayVideo}
                  videoUri={poi.videoUri}
                  position={
                    poi.position * 100 * sceneWidth * getPaddedWidthFactor(sceneWidth) +
                    PADDING_IN_VW
                  }
                  windowWidth={windowWidth}
                />
              )
            })}
          </Background>
        </motion.div>

        {videoDetails.videoUri && (
          <VideoPlayer
            key={"video"}
            videoUri={videoDetails.videoUri}
            shallPlayVideo={videoDetails.shallPlayVideo}
            title={videoDetails.title}
            stopVideo={() => {
              setVideoDetails({
                videoUri: videoDetails.videoUri,
                shallPlayVideo: false,
              })
            }}
          />
        )}
      </AnimatePresence>

      <Minimap
        isVisible={appState !== INTRO_START}
        sceneWidth={sceneWidth}
        parallaxContainerRef={parallaxContainerRef}
        windowWidth={windowWidth}
      />
    </>
  )
}

function getLayerWidthInPercent(speed, sceneWidth) {
  return sceneWidth * 100 * (Math.abs(speed) + 1)
}

function getPaddedWidthFactor(sceneWidth) {
  return (100 - (2 * PADDING_IN_VW) / sceneWidth) / 100
}

export default Parallax
